/**
 * groupThreadLiveTurnsRepository — the client's listener on the shared LIVE-TURN
 * mirror (§11 WS-2 of docs/PLAN-mid-turn-fold-in-20260915.md; T-R13, 2026-09-20).
 *
 * One floored `onSnapshot` on `group_thread_messages/{threadId}/live` — the docs the
 * server writes while Prest0n works for someone in the room (PR-B). Every snapshot is
 * handed on, the EMPTY one included: absence is the information that a turn ended.
 *
 * Built on the reactions listener (messageReactionsRepository.ts), not the receipts
 * one: the rules floor a `list` on `orderTs` (the asker's own message stamp), so a
 * query without `where('orderTs','>=',floor)` is refused wholesale — and a floor the
 * membership read has not published yet (HISTORY_FLOOR_FAIL_CLOSED) is "not ready",
 * never "listen to nothing" (the 2026-09-11 latch). The read master switch runs
 * first: zero Firestore access while it is off.
 */
import { onSnapshot, query, where } from 'firebase/firestore';
import { collection, db } from './firestoreUtils';
import { resolveGroupThreadReadEnabled } from '../services/groupThreads/featureFlags';
import { getGroupThreadHistoryFloor, HISTORY_FLOOR_FAIL_CLOSED } from '../services/groupThreads/historyFloors';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';
import { fromLiveTurnDoc, GROUP_LIVE_TURNS_COLLECTION, type LiveTurnView } from '../services/groupThreads/liveTurns';

const GROUP_STORE = 'group_thread_messages';

export function subscribeThreadLiveTurns(
  threadId: string,
  onLiveTurns: (docs: LiveTurnView[]) => void,
  opts?: { accountId?: string | null },
): () => void {
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return () => {};
  const id = (threadId ?? '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    const floor = getGroupThreadHistoryFloor(id);
    // An unpublished floor is "not ready", not "show nothing" (the reactions
    // listener's 2026-09-11 lesson): a query on the sentinel succeeds with an
    // empty snapshot and the error ladder never runs.
    if (floor === HISTORY_FLOOR_FAIL_CLOSED) { scheduleRetry(); return; }
    try {
      const floored = query(collection(db, GROUP_STORE, id, GROUP_LIVE_TURNS_COLLECTION), where('orderTs', '>=', floor));
      const unsubscribe = onSnapshot(
        floored,
        (snap) => {
          retryIndex = 0;
          const nowMs = Date.now();
          const out: LiveTurnView[] = [];
          for (const d of snap.docs) {
            const parsed = fromLiveTurnDoc(d.id, d.data(), nowMs);
            if (parsed) out.push(parsed);
          }
          onLiveTurns(out);
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      scheduleRetry();
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    try { detachInner?.(); } catch { /* already detached */ }
  };
}
